import { API } from "aws-amplify";
import { rateFallback } from "../libs/shared/seat-helpers";

let cachedSettings = null;
let cacheTimestamp = 0;
let fetchPromise = null;
const TTL = 2 * 60 * 1000; // 2 minutes in ms

const fetchSettingsBatch = async () => {
  try {
    const response = await API.get("v2", "marketplace/setting/frontend");
    cachedSettings = response;
    cacheTimestamp = Date.now();
    fetchPromise = null;
    return response;
  } catch (error) {
    fetchPromise = null;
    return {};
  }
};

const getSettings = async (force = false) => {
  const isExpired = Date.now() - cacheTimestamp > TTL;
  if (force || !cachedSettings || isExpired) {
    if (!fetchPromise) {
      fetchPromise = fetchSettingsBatch();
    }
    return fetchPromise;
  }
  return cachedSettings;
};

export const getThreeDSStatus = async () => {
  try {
    const settings = await getSettings();
    return settings.threeDSStatus || "on";
  } catch (error) {
    // return "on" in case ther is an error when getting 3ds status 
    return "on";
  }
};

export const getMaintenanceStatus = async () => {
  try {
    const settings = await getSettings();
    return settings.maintenanceStatus || "off";
  } catch (error) {
    // return "off" in case ther is an error when getting maintenance status 
    return "off";
  }
};

export const getAutoNotifyWindow = async () => {
  try {
    const settings = await getSettings();
    return settings.autoNotifyWindow || null;
  } catch (error) {
    return null;
  }
};

export const getInstantDeliveryPopupWindow = async () => {
  try {
    const settings = await getSettings();
    return settings.instantDeliveryPopupWindow || null;
  } catch (error) {
    return null;
  }
};

export const getNoticeBanner = async () => {
  try {
    const settings = await getSettings();
    const notice = settings.noticeBanner || {};
    return {
      enabled: notice.enabled || false,
      message: (notice.message || "").trim(),
      messageFr: (notice.messageFr || "").trim(),
    };
  } catch (error) {
    return { enabled: false, message: "", messageFr: "" };
  }
};

export const getCurrencyExchangeRate = async () => {
  try {
    const response = await API.get("v2", `marketplace/ticketEvo/currencyRate`);
    return response.currencyRate;
  } catch (error) {
    return { timestamp: 1670452262, rate: rateFallback }
  }
};

export const getWalletPaymentMethodsStatus = async () => {
  try {
    const settings = await getSettings();
    const wallet = settings.walletPaymentMethods || {};
    return {
      applePay: wallet.applePay || false,
      googlePay: wallet.googlePay || false,
    };
  } catch (error) {
    return {
      applePay: false,
      googlePay: false,
    };
  }
};

export const getEmergencyMode = async () => {
  try {
    const settings = await getSettings();
    return settings.emergencyMode || "False";
  } catch (error) {
    return "False";
  }
};

export const getTnListingsEnabledTeams = async () => {
  try {
    await getSettings();
    return getCachedTnListingsEnabledTeams();
  } catch (error) {
    return null;
  }
};

export const getCachedTnListingsEnabledTeams = () => {
  return cachedSettings ? (cachedSettings.tnListingsEnabledTeams || []) : null;
};

export const isTnEnabledForTeam = (enabledTnTeams, teamSlug) => {
  if (!enabledTnTeams || !Array.isArray(enabledTnTeams)) {
    return false;
  }
  return enabledTnTeams.includes(teamSlug);
};

// Awaits the settings fetch: callers must not read the cache before it has loaded,
// or an enabled team looks disabled.
export const shouldFetchTnListingsForGame = async (game) => {
  if (!game || !game.ticketNetworkId) {
    return false;
  }
  return isTnEnabledForTeam(await getTnListingsEnabledTeams(), game.homeTeamSlug);
};

export const isAttEligibleTeam = async (homeTeamSlug) => {
  if (!homeTeamSlug) {
    return false;
  }
  try {
    const settings = await getSettings();
    const att = settings.attSettings || {};
    const teams = Array.isArray(att.teams) ? att.teams : [];
    return Boolean(att.enabled) && teams.indexOf(homeTeamSlug) !== -1;
  } catch (error) {
    return false;
  }
};

export const DEFAULT_TE_LATER_FULFILLMENT_TEAMS = ["edmonton-wjc"];
export const DEFAULT_TE_LATER_FULFILLMENT_COPY = {
  enLabel: "Tickets Delivered Closer to Event",
  frLabel: "Billets livrés à l'approche du match",
  enTitle: "Ticket Availability Timing",
  frTitle: "Délai de disponibilité des billets",
  enBody:
    "Tickets for this event will become available closer to the event date. You will receive an email notification as soon as your tickets are ready for transfer.",
  frBody:
    "Les billets pour cet événement seront disponibles plus près de la date du match. Vous recevrez une notification par courriel dès que vos billets seront prêts à être transférés.",
  labelEn: "Tickets Delivered Closer to Event",
  labelFr: "Billets livrés à l'approche du match",
  modalTitleEn: "Ticket Availability Timing",
  modalTitleFr: "Délai de disponibilité des billets",
  modalBodyEn:
    "Tickets for this event will become available closer to the event date. You will receive an email notification as soon as your tickets are ready for transfer.",
  modalBodyFr:
    "Les billets pour cet événement seront disponibles plus près de la date du match. Vous recevrez une notification par courriel dès que vos billets seront prêts à être transférés.",
};

const _resolveCopyObject = (copy) => {
  if (!copy || typeof copy !== "object") {
    return DEFAULT_TE_LATER_FULFILLMENT_COPY;
  }
  const enLabel = (copy.enLabel || copy.labelEn || copy.label || DEFAULT_TE_LATER_FULFILLMENT_COPY.enLabel).trim();
  const frLabel = (copy.frLabel || copy.labelFr || copy.label_fr || DEFAULT_TE_LATER_FULFILLMENT_COPY.frLabel).trim();
  const enTitle = (copy.enTitle || copy.modalTitleEn || copy.modalTitle || DEFAULT_TE_LATER_FULFILLMENT_COPY.enTitle).trim();
  const frTitle = (copy.frTitle || copy.modalTitleFr || copy.modalTitle_fr || DEFAULT_TE_LATER_FULFILLMENT_COPY.frTitle).trim();
  const enBody = (copy.enBody || copy.modalBodyEn || copy.modalBody || DEFAULT_TE_LATER_FULFILLMENT_COPY.enBody).trim();
  const frBody = (copy.frBody || copy.modalBodyFr || copy.modalBody_fr || DEFAULT_TE_LATER_FULFILLMENT_COPY.frBody).trim();
  return {
    enLabel,
    frLabel,
    enTitle,
    frTitle,
    enBody,
    frBody,
    labelEn: enLabel,
    labelFr: frLabel,
    modalTitleEn: enTitle,
    modalTitleFr: frTitle,
    modalBodyEn: enBody,
    modalBodyFr: frBody,
  };
};

export const getTeLaterFulfillmentTeams = async () => {
  try {
    const settings = await getSettings();
    if (Array.isArray(settings.teLaterFulfillmentTeams) && settings.teLaterFulfillmentTeams.length > 0) {
      return settings.teLaterFulfillmentTeams;
    }
    return DEFAULT_TE_LATER_FULFILLMENT_TEAMS;
  } catch (error) {
    return DEFAULT_TE_LATER_FULFILLMENT_TEAMS;
  }
};

export const getCachedTeLaterFulfillmentTeams = () => {
  if (cachedSettings && Array.isArray(cachedSettings.teLaterFulfillmentTeams) && cachedSettings.teLaterFulfillmentTeams.length > 0) {
    return cachedSettings.teLaterFulfillmentTeams;
  }
  return DEFAULT_TE_LATER_FULFILLMENT_TEAMS;
};

export const getTeLaterFulfillmentCopy = async () => {
  try {
    const settings = await getSettings();
    return _resolveCopyObject(settings.teLaterFulfillmentCopy);
  } catch (error) {
    return DEFAULT_TE_LATER_FULFILLMENT_COPY;
  }
};

export const getCachedTeLaterFulfillmentCopy = () => {
  if (cachedSettings && cachedSettings.teLaterFulfillmentCopy && typeof cachedSettings.teLaterFulfillmentCopy === "object") {
    return _resolveCopyObject(cachedSettings.teLaterFulfillmentCopy);
  }
  return DEFAULT_TE_LATER_FULFILLMENT_COPY;
};

export const isLaterFulfillmentEligibleTeam = async (homeTeamSlug) => {
  if (!homeTeamSlug) {
    return false;
  }
  try {
    const teams = await getTeLaterFulfillmentTeams();
    return Array.isArray(teams) && teams.includes(homeTeamSlug);
  } catch (error) {
    return false;
  }
};

export const getReminderGameNotificationTeams = async () => {
  try {
    const settings = await getSettings();
    if (Array.isArray(settings.reminderGameNotificationTeams)) {
      return settings.reminderGameNotificationTeams;
    }
    return [
      "calgary-flames",
      "winnipeg-jets",
      "edmonton-oilers",
      "ottawa-senators",
      "toronto-blue-jays",
      "winnipeg-blue-bombers",
      "calgary-stampeders",
    ];
  } catch (error) {
    return [
      "calgary-flames",
      "winnipeg-jets",
      "edmonton-oilers",
      "ottawa-senators",
      "toronto-blue-jays",
      "winnipeg-blue-bombers",
      "calgary-stampeders",
    ];
  }
};

export const isReminderGameNotificationTeam = async (homeTeamSlug) => {
  if (!homeTeamSlug) {
    return false;
  }
  try {
    const teams = await getReminderGameNotificationTeams();
    return Array.isArray(teams) && teams.includes(homeTeamSlug);
  } catch (error) {
    return false;
  }
};

export const _resetCache = () => {
  cachedSettings = null;
  cacheTimestamp = 0;
  fetchPromise = null;
};

export const refreshSettings = () => getSettings(true);

